import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiTrash2, FiClock, FiSettings, FiUsers } from 'react-icons/fi';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const hoy = () => new Date().toISOString().slice(0, 10);
const VACIO = { fecha: hoy(), hora: '', personas: 2, clienteNombre: '', clienteTelefono: '', notas: '' };
const ESTADOS = { pendiente: 'Pendiente', confirmada: 'Confirmada', sentada: 'Sentada', no_presentado: 'No presentado', cancelada: 'Cancelada' };
const BADGE = { pendiente: 'badge-warning', confirmada: 'badge-ok', sentada: 'badge-info', no_presentado: 'badge', cancelada: 'badge-danger' };

export default function Reservas() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';
  const [dia, setDia] = useState(hoy());
  const [ocupacion, setOcupacion] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);
  const [cfgModal, setCfgModal] = useState(false);
  const [aforoMax, setAforoMax] = useState(40);

  const puedeCrear = puede(usuario, 'reservas', 'crear');
  const puedeEliminar = puede(usuario, 'reservas', 'eliminar');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get('/reservas/ocupacion', { params: { fecha: dia } });
      setOcupacion(data);
      setAforoMax(data.aforoMaximo);
    } catch { /* noop */ } finally { setCargando(false); }
  }, [dia]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const onCrear = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post('/reservas', { ...form, personas: Number(form.personas) });
      setModal(false);
      setForm({ ...VACIO, fecha: dia });
      cargar();
    } catch (err) {
      const d = err.response?.data;
      alert(d?.detalle ? `${d.error}. Disponibles: ${d.detalle.disponible}` : (d?.error || 'Error al reservar'));
    } finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta reserva?')) return;
    try { await api.delete(`/reservas/${id}`); cargar(); } catch { alert('No se pudo eliminar'); }
  };

  const cambiarEstado = async (id, estado) => {
    try { await api.put(`/reservas/${id}`, { estado }); cargar(); } catch { alert('No se pudo actualizar'); }
  };

  const guardarConfig = async (e) => {
    e.preventDefault();
    try { await api.put('/reservas/config', { aforoMaximo: Number(aforoMax) }); setCfgModal(false); cargar(); }
    catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const pct = ocupacion ? Math.min(100, Math.round((ocupacion.totalPersonas / ocupacion.aforoMaximo) * 100)) : 0;

  return (
    <div className="page">
      <div className="page-head">
        <div><h1 className="page-title">Reservas y aforo</h1><p className="page-subtitle">Ocupación en tiempo real y previsión</p></div>
        <div className="head-actions">
          {esAdmin && <button className="btn" onClick={() => setCfgModal(true)}><FiSettings /> Aforo</button>}
          {puedeCrear && <button className="btn btn-primary" onClick={() => { setForm({ ...VACIO, fecha: dia }); setModal(true); }}><FiPlus /> Nueva reserva</button>}
        </div>
      </div>

      <div className="toolbar">
        <label className="field" style={{ maxWidth: 220 }}><span>Día</span><input type="date" value={dia} onChange={(e) => setDia(e.target.value)} /></label>
      </div>

      {ocupacion && (
        <div className="kpi-grid">
          <div className="kpi-card"><span className="kpi-label">Aforo máximo</span><span className="kpi-value">{ocupacion.aforoMaximo}</span></div>
          <div className="kpi-card"><span className="kpi-label">Reservado</span><span className="kpi-value">{ocupacion.totalPersonas}</span></div>
          <div className="kpi-card"><span className="kpi-label">Disponible</span><span className="kpi-value">{ocupacion.disponible}</span></div>
          <div className="kpi-card">
            <span className="kpi-label">Ocupación</span>
            <span className="kpi-value">{pct}%</span>
            <div className="barra"><div className="barra-fill" style={{ width: `${pct}%`, background: pct >= 90 ? '#e5484d' : pct >= 70 ? '#f5a623' : '#30a46c' }} /></div>
          </div>
        </div>
      )}

      {ocupacion && Object.keys(ocupacion.porFranja || {}).length > 0 && (
        <div className="franjas">
          {Object.entries(ocupacion.porFranja).sort().map(([hora, n]) => (
            <div key={hora} className="franja-chip"><FiClock /> {hora} · <strong>{n}</strong> pers.</div>
          ))}
        </div>
      )}

      <h3 className="permisos-titulo"><FiUsers /> Reservas del día</h3>
      {cargando ? <p className="empty">Cargando…</p> : !ocupacion || ocupacion.reservas.length === 0 ? <p className="empty">No hay reservas este día.</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Hora</th><th>Cliente</th><th>Personas</th><th>Teléfono</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {[...ocupacion.reservas].sort((a, b) => a.hora.localeCompare(b.hora)).map((r) => (
                <tr key={r._id}>
                  <td data-label="Hora">{r.hora}</td>
                  <td data-label="Cliente"><strong>{r.clienteNombre}</strong></td>
                  <td data-label="Personas">{r.personas}</td>
                  <td data-label="Teléfono">{r.clienteTelefono || '—'}</td>
                  <td data-label="Estado">
                    <select className="mini-select" value={r.estado} onChange={(e) => cambiarEstado(r._id, e.target.value)}>
                      {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="acciones">{puedeEliminar && <button className="icon-btn danger" onClick={() => eliminar(r._id)}><FiTrash2 /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo="Nueva reserva">
        <form onSubmit={onCrear} className="form-grid">
          <label className="field"><span>Fecha *</span><input type="date" required value={form.fecha} onChange={set('fecha')} /></label>
          <label className="field"><span>Hora *</span><input type="time" required value={form.hora} onChange={set('hora')} /></label>
          <label className="field"><span>Personas *</span><input type="number" min="1" required value={form.personas} onChange={set('personas')} /></label>
          <label className="field"><span>Teléfono</span><input value={form.clienteTelefono} onChange={set('clienteTelefono')} /></label>
          <label className="field span-2"><span>Cliente *</span><input required value={form.clienteNombre} onChange={set('clienteNombre')} /></label>
          <label className="field span-2"><span>Notas</span><input value={form.notas} onChange={set('notas')} /></label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Reservar'}</button>
          </div>
        </form>
      </Modal>

      <Modal abierto={cfgModal} onCerrar={() => setCfgModal(false)} titulo="Configurar aforo" ancho={400}>
        <form onSubmit={guardarConfig} className="form-grid">
          <label className="field span-2"><span>Aforo máximo por día</span><input type="number" min="1" value={aforoMax} onChange={(e) => setAforoMax(e.target.value)} /></label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setCfgModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Guardar</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
